'use client'

import { useCallback, useMemo, useState } from 'react'

import { useAbTest, useTrackAbTest } from '@marketplace-web/feature-flags/ab-tests-data'
import { ConversationMessage } from '@marketplace-web/messaging/conversation-messaging-data'

import { isMessage } from '../../utils'
import PhotosDialog from './PhotosDialog'

const usePhotosDialog = (messages: Array<ConversationMessage>) => {
  const imageOpeningInConversationExperiment = useAbTest('dd_tx_image_opening_in_conversation')
  const isImageOpeningInConversationExperimentOn =
    imageOpeningInConversationExperiment?.variant === 'on'

  const [photosDialogSlide, setPhotosDialogSlide] = useState<number | null>(null)

  const nonHiddenPhotos = useMemo(
    () =>
      messages.flatMap(conversationMessage => {
        if (!isMessage(conversationMessage)) return []

        return conversationMessage.message.photos.filter(photo => !photo.isHidden)
      }),
    [messages],
  )

  const hasNonHiddenPhotos = nonHiddenPhotos.length > 0

  useTrackAbTest(imageOpeningInConversationExperiment, hasNonHiddenPhotos)

  const idToIndexMap = useMemo(
    () => Object.fromEntries(nonHiddenPhotos.map((photo, index) => [photo.id, index])),
    [nonHiddenPhotos],
  )

  const closePhotosDialog = useCallback(() => setPhotosDialogSlide(null), [])
  const openPhotoDialog = useCallback(
    (photoId: number) => {
      const index = idToIndexMap[photoId]
      if (index === undefined) return
      setPhotosDialogSlide(index)
    },
    [idToIndexMap],
  )

  const handleSlideInteract = useCallback((slide: number) => setPhotosDialogSlide(slide), [])

  return {
    onThumbnailClick: isImageOpeningInConversationExperimentOn ? openPhotoDialog : undefined,
    photosDialogElement: isImageOpeningInConversationExperimentOn && hasNonHiddenPhotos && (
      <PhotosDialog
        onClose={closePhotosDialog}
        onSlideInteract={handleSlideInteract}
        slide={photosDialogSlide}
        photos={nonHiddenPhotos}
      />
    ),
  }
}

export default usePhotosDialog
